import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';

/**
 * Indicações (etapa 12): sócio ou conselheiro indica uma empresa que se
 * beneficiaria do Conselheiro. A indicação chega à equipe comercial da Hack
 * (no CRM do HackOS), e a situação volta para cá.
 */

type Indicacao = {
  id: string;
  papel: 'SOCIO' | 'CONSELHEIRO';
  empresa_nome: string;
  contato_nome: string;
  situacao: 'RECEBIDA' | 'EM_CONVERSA' | 'VIROU_CLIENTE' | 'NAO_SEGUIU';
  enviada_em: string | null;
  created_at: string;
};

export type OrigemDaIndicacao = { empresa_id: string | null; rotulo: string };

const SITUACAO: Record<Indicacao['situacao'], { texto: string; cor: string }> = {
  RECEBIDA: { texto: 'Recebida', cor: 'bg-neutral-100 text-neutral-800' },
  EM_CONVERSA: { texto: 'Em conversa', cor: 'bg-amber-100 text-amber-900' },
  VIROU_CLIENTE: { texto: 'Virou cliente', cor: 'bg-emerald-100 text-emerald-900' },
  NAO_SEGUIU: { texto: 'Não seguiu', cor: 'bg-neutral-200 text-neutral-700' },
};

const campo = 'rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';

export function Indicacoes({ origens }: { origens: OrigemDaIndicacao[] }) {
  const qc = useQueryClient();
  const chave = ['conselheiro-indicacoes'];
  const q = useQuery({ queryKey: chave, queryFn: async () => (await api.get<Indicacao[]>('/conselheiro/indicacoes')).data });
  const [origem, setOrigem] = useState(0);
  const [empresa, setEmpresa] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [contato, setContato] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const indicar = useMutation({
    mutationFn: async () =>
      (
        await api.post<Indicacao[]>('/conselheiro/indicacoes', {
          empresa_id: origens[origem]?.empresa_id ?? null,
          empresa: { nome: empresa.trim(), cnpj: cnpj.trim() || null },
          contato: { nome: contato.trim(), email: email.trim() || null, telefone: telefone.trim() || null },
          motivo: motivo.trim() || null,
        })
      ).data,
    onSuccess: (d) => {
      qc.setQueryData(chave, d);
      setErro(null);
      setOk(true);
      setEmpresa('');
      setCnpj('');
      setContato('');
      setEmail('');
      setTelefone('');
      setMotivo('');
    },
    onError: (e) => {
      setOk(false);
      setErro(extractError(e).message);
    },
  });

  const pronto = empresa.trim().length >= 2 && contato.trim().length >= 2 && (email.trim() || telefone.trim());

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-ha-muted">
        Conhece uma empresa que ganharia com um conselho como o seu? Indique: a equipe da Hack entra em contato em seu nome, e
        você acompanha aqui o que aconteceu.
      </p>
      <form
        className="grid gap-2 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto) indicar.mutate();
        }}
      >
        {origens.length > 1 ? (
          <label className="flex flex-col gap-1 text-xs text-ha-muted md:col-span-2">
            Indicar como
            <select className={campo} value={origem} onChange={(e) => setOrigem(Number(e.target.value))}>
              {origens.map((o, i) => (
                <option key={o.rotulo} value={i}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          Empresa
          <input className={campo} value={empresa} onChange={(e) => setEmpresa(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          CNPJ (opcional)
          <input className={campo} value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ha-muted">
          Quem procurar
          <input className={campo} value={contato} onChange={(e) => setContato(e.target.value)} placeholder="Nome" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-xs text-ha-muted">
            E-mail
            <input className={campo} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ha-muted">
            Telefone
            <input className={campo} value={telefone} onChange={(e) => setTelefone(e.target.value)} />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-xs text-ha-muted md:col-span-2">
          Por que indica (opcional)
          <textarea className={campo} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        <div className="flex items-center gap-3 md:col-span-2">
          <button type="submit" className={botao} style={{ background: 'var(--primary)' }} disabled={!pronto || indicar.isPending}>
            Indicar
          </button>
          {ok ? <span className="text-xs text-emerald-700">Indicação enviada. Obrigado!</span> : null}
          {!pronto ? <span className="text-xs text-ha-muted">Empresa, nome e e-mail ou telefone de quem procurar.</span> : null}
        </div>
      </form>
      {erro ? <p className="text-sm text-red-600">{erro}</p> : null}

      {q.data && q.data.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ha-muted">Suas indicações</p>
          <ul className="flex flex-col gap-1.5">
            {q.data.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  <span className="font-medium text-ha-text">{i.empresa_nome}</span>{' '}
                  <span className="text-xs text-ha-muted">
                    {i.contato_nome} · {new Date(i.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </span>
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${i.enviada_em ? SITUACAO[i.situacao].cor : 'bg-neutral-100 text-neutral-700'}`}>
                  {i.enviada_em ? SITUACAO[i.situacao].texto : 'A caminho da equipe'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
